<template>
  <div
    :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'"
    class="modal fade ai-help-modal qbank-section"
    id="help-modal"
    tabindex="-1"
    role="dialog"
    aria-labelledby="aiHelpModalTitle"
    aria-hidden="true"
  >
    <div
      class="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable"
      role="document"
    >
      <div class="modal-content">
        <input type="hidden" id="questionHelpId" />

        <div class="modal-header">
          <div class="header-content">
            <div class="header-icon">
              <i class="fa fa-graduation-cap"></i>
            </div>
            <div class="header-text">
              <h5 class="modal-title" id="aiHelpModalTitle">
                {{ $t("qbank.ai_help.teach_me_how") }}
              </h5>
              <span v-if="started" class="step-indicator">
                {{
                  $t("qbank.ai_help.step_of", {
                    current: currentStep,
                    total: 4,
                  })
                }}
              </span>
            </div>
          </div>
          <button
            type="button"
            class="btn-close-custom"
            data-bs-dismiss="modal"
            @click="resetState"
          >
            <i class="fa fa-times"></i>
          </button>
        </div>

        <!-- Progress Bar -->
        <div v-if="started" class="progress-container">
          <div class="progress-bar">
            <div
              class="progress-fill"
              :style="{ width: (currentStep / 4) * 100 + '%' }"
            ></div>
          </div>
          <div class="step-labels">
            <span
              v-for="step in 4"
              :key="step"
              class="step-label"
              :class="{
                active: currentStep >= step,
                current: currentStep === step,
              }"
            >
              {{ step }}
            </span>
          </div>
        </div>

        <div class="modal-body">
          <!-- Loading State -->
          <div v-if="isLoading" class="loading-state">
            <div class="loading-animation">
              <div class="loading-circle"></div>
              <div class="loading-circle"></div>
              <div class="loading-circle"></div>
            </div>
            <p class="loading-text">
              {{ $t("qbank.ai_help.loading_explanation") }}
            </p>
          </div>

          <!-- Start State -->
          <div v-else-if="!started" class="start-state">
            <div class="start-icon">
              <img src="assets/img/chat.png" alt="AI Help" />
            </div>
            <h4 class="start-title">
              {{ $t("qbank.ai_help.ready_to_learn") }}
            </h4>
            <p class="start-subtitle">
              {{ $t("qbank.ai_help.step_by_step_guide") }}
            </p>
            <button class="btn-start" @click="startExplanation">
              <i class="fa fa-play"></i>
              <span>{{ $t("qbank.ai_help.start") }}</span>
            </button>
          </div>

          <!-- Content State -->
          <div v-else class="content-state">
            <div class="step-header">
              <span class="step-badge">{{ currentStep }}</span>
              <h5 class="step-title">{{ steps[currentStep - 1].title }}</h5>
            </div>

            <div class="content-wrapper" ref="contentWrapper">
              <!-- Content Loading Animation -->
              <div v-if="isContentLoading" class="content-loading">
                <div class="skeleton-lines">
                  <div class="skeleton-line" style="width: 95%"></div>
                  <div class="skeleton-line" style="width: 80%"></div>
                  <div class="skeleton-line" style="width: 90%"></div>
                  <div class="skeleton-line" style="width: 70%"></div>
                  <div class="skeleton-line" style="width: 85%"></div>
                </div>
                <div class="loading-shimmer"></div>
              </div>

              <!-- Actual Content -->
              <div
                v-show="!isContentLoading"
                :dir="isEnglish ? 'ltr' : 'rtl'"
                class="ai-content content-math fade-in"
                :class="{ 'text-left': isEnglish, 'text-right': !isEnglish }"
                ref="mathContent"
                v-html="formattedDisplayText"
              ></div>
            </div>
          </div>
        </div>

        <!-- Footer Navigation -->
        <div v-if="started && !isLoading" class="modal-footer">
          <button
            class="btn-nav btn-prev"
            :disabled="currentStep <= 1"
            @click="prevStep"
          >
            <i class="fa fa-arrow-right"></i>
            <span>{{ $t("qbank.ai_help.previous") }}</span>
          </button>

          <div class="step-dots">
            <span
              v-for="step in 4"
              :key="step"
              class="dot"
              :class="{
                active: currentStep === step,
                completed: currentStep > step,
              }"
            ></span>
          </div>

          <button
            v-if="currentStep < 4"
            class="btn-nav btn-next"
            @click="nextStep"
          >
            <span>{{ $t("qbank.ai_help.next") }}</span>
            <i class="fa fa-arrow-left"></i>
          </button>
          <button
            v-else
            class="btn-nav btn-finish"
            data-bs-dismiss="modal"
            @click="resetState"
          >
            <span>{{ $t("qbank.ai_help.finish") }}</span>
            <i class="fa fa-check"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiUrl } from "@/utils/api";

export default {
  name: "HelpAiModal",
  data() {
    return {
      token: localStorage.getItem("access_token"),
      currentStep: 1,
      stepText: "",
      guidance: "",
      isLoading: false,
      started: false,
      displayedText: "",
      isEnglish: false,
      isContentLoading: false,
      cachedSteps: {}, // Cache للخطوات المحملة
    };
  },
  computed: {
    steps() {
      return [
        { title: this.$t("qbank.ai_help.understand_question") },
        { title: this.$t("qbank.ai_help.analyze_problem") },
        { title: this.$t("qbank.ai_help.evaluate_options") },
        { title: this.$t("qbank.ai_help.explain_correct_answer") },
      ];
    },
    formattedDisplayText() {
      if (!this.displayedText) return "";
      return this.formatMathContent(this.displayedText);
    },
  },
  methods: {
    formatMathContent(text) {
      if (!text) return "";

      // 1. إصلاح الـ backslashes المزدوجة للـ LaTeX
      let formatted = text.replace(/\\\\/g, "\\");

      // 2. إزالة \ar من المحتوى
      formatted = formatted.replace(/\\ar\b/g, "");
      formatted = formatted.replace(/\\ar/g, "");

      // 2. تحويل صيغ LaTeX المختلفة لصيغة موحدة
      // تحويل \[ \] إلى $$ $$
      formatted = formatted.replace(/\\\[/g, "$$");
      formatted = formatted.replace(/\\\]/g, "$$");

      // تحويل \( \) إلى $ $
      formatted = formatted.replace(/\\\(/g, "$");
      formatted = formatted.replace(/\\\)/g, "$");

      // 3. إصلاح المعادلات المعقدة - حماية المحتوى داخل $ $
      // تأكد من عدم كسر المعادلات عبر الأسطر
      formatted = formatted.replace(/\$\$([^$]+)\$\$/g, (match, content) => {
        // إزالة الأسطر الجديدة داخل المعادلات block
        const cleanContent = content.replace(/\n/g, " ").trim();
        return `<div class="math-block">$$${cleanContent}$$</div>`;
      });

      // 4. معالجة المعادلات inline
      formatted = formatted.replace(/\$([^$\n]+)\$/g, (match, content) => {
        return `<span class="math-inline">$${content.trim()}$</span>`;
      });

      // 5. تنسيق القوائم المرقمة والنقطية
      formatted = formatted.replace(
        /^(\d+)\.\s+/gm,
        '<span class="list-number">$1.</span> '
      );
      formatted = formatted.replace(
        /^[-•]\s+/gm,
        '<span class="list-bullet">•</span> '
      );

      // 6. تنسيق العناوين (النص بين **)
      formatted = formatted.replace(
        /\*\*([^*]+)\*\*/g,
        '<strong class="highlight">$1</strong>'
      );

      // 7. إزالة الأسطر الفارغة الزائدة
      formatted = formatted.replace(/\n{3,}/g, "\n\n");

      // 8. تحويل الأسطر الجديدة إلى <br> مع الحفاظ على الفقرات
      formatted = formatted
        .split("\n\n")
        .map(
          (para) =>
            `<p class="content-paragraph">${para.replace(/\n/g, "<br>")}</p>`
        )
        .join("");

      return formatted;
    },

    detectLanguage(text) {
      // إزالة رموز LaTeX والأرقام للكشف عن اللغة
      const cleanText = text
        .replace(/\$[^$]+\$/g, "") // إزالة المعادلات
        .replace(/\\\w+/g, "") // إزالة أوامر LaTeX
        .replace(/[0-9.,;!?():\s\-\*\"\'\n•\[\]{}=+<>]/g, "")
        .trim();

      const hasArabic = /[\u0600-\u06FF]/.test(cleanText);
      const hasEnglish = /[A-Za-z]/.test(cleanText);

      // إذا كان فيه عربي، نعتبره عربي
      if (hasArabic) return false;
      // إذا كان إنجليزي فقط
      if (hasEnglish && !hasArabic) return true;
      // افتراضي: عربي
      return false;
    },

    async getStepText() {
      const questionId = document.getElementById("questionHelpId").value;
      if (!questionId) return;

      // تحقق من الـ cache أولاً
      const cacheKey = `${questionId}_${this.currentStep}`;
      if (this.cachedSteps[cacheKey]) {
        this.stepText = this.cachedSteps[cacheKey];
        this.isEnglish = this.detectLanguage(this.stepText);
        this.showContent();
        return;
      }

      this.isLoading = true;
      this.stepText = "";
      this.displayedText = "";

      try {
        const response = await fetch(
          `${apiUrl}/qbanks/questions/${questionId}/ai-guidance`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${this.token}`,
            },
            body: JSON.stringify({
              current_step: this.currentStep,
              student_answer: "",
            }),
          }
        );

        const data = await response.json();

        if (!this.guidance) {
          this.guidance = data.guidance || "";
        }

        this.stepText =
          data.guidance || this.$t("qbank.ai_help.no_explanation");

        // حفظ في الـ cache
        this.cachedSteps[cacheKey] = this.stepText;

        // كشف اللغة
        this.isEnglish = this.detectLanguage(this.stepText);

        this.showContent();
      } catch (err) {
        console.error("AI Guidance Error:", err);
        this.stepText = this.$t("qbank.ai_help.error_loading");
        this.displayedText = this.stepText;
      } finally {
        this.isLoading = false;
      }
    },

    showContent() {
      // عرض تأثير التحميل
      this.isContentLoading = true;

      // تنظيف النص من \ar
      const cleanText = this.stepText
        .replace(/\\ar\b/g, "")
        .replace(/\\ar/g, "");

      this.displayedText = cleanText;

      // انتظار قليلاً ثم تنفيذ MathJax وإظهار المحتوى
      this.$nextTick(() => {
        setTimeout(() => {
          this.renderMath();
          // إخفاء التحميل وإظهار المحتوى بعد تجهيز MathJax
          setTimeout(() => {
            this.isContentLoading = false;
          }, 300);
        }, 100);
      });
    },

    renderMath() {
      if (window.MathJax && window.MathJax.typesetPromise) {
        this.$nextTick(() => {
          setTimeout(() => {
            const container = this.$refs.mathContent;
            if (container) {
              // إزالة \ar قبل معالجة MathJax
              container.innerHTML = container.innerHTML
                .replace(/\\ar\b/g, "")
                .replace(/\\ar/g, "");

              // مسح الـ typeset السابق
              MathJax.typesetClear([container]);
              // إعادة الـ typeset
              MathJax.typesetPromise([container])
                .then(() => {
                  // إزالة أي \ar متبقية بعد MathJax
                  container.innerHTML = container.innerHTML
                    .replace(/\\ar\b/g, "")
                    .replace(/\\ar/g, "");

                  // التمرير للأعلى بعد العرض
                  if (this.$refs.contentWrapper) {
                    this.$refs.contentWrapper.scrollTop = 0;
                  }
                })
                .catch((err) => console.log("MathJax error:", err));
            }
          }, 50);
        });
      }
    },

    startExplanation() {
      this.started = true;
      this.currentStep = 1;
      this.cachedSteps = {}; // مسح الـ cache عند البدء من جديد
      this.getStepText();
    },

    nextStep() {
      if (this.currentStep < 4) {
        this.currentStep++;
        this.getStepText();
      }
    },

    prevStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
        this.getStepText();
      }
    },

    resetState() {
      this.currentStep = 1;
      this.stepText = "";
      this.guidance = "";
      this.started = false;
      this.displayedText = "";
      this.isContentLoading = false;
      this.cachedSteps = {};
    },
  },
};
</script>

<style scoped>
/* Modal Container */
.ai-help-modal .modal-dialog {
  max-width: 800px;
  margin: 1rem auto;
}

.ai-help-modal .modal-content {
  border: none;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  max-height: 90vh;
}

/* Header */
.ai-help-modal .modal-header {
  background: linear-gradient(135deg, #07c232 0%, #05a028 100%);
  border-bottom: none;
  padding: 1.25rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-icon {
  width: 45px;
  height: 45px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-icon i {
  font-size: 1.4rem;
  color: white;
}

.header-text {
  display: flex;
  flex-direction: column;
}

.modal-title {
  color: white;
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0;
}

.step-indicator {
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.85rem;
}

.btn-close-custom {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-close-custom:hover {
  background: rgba(255, 255, 255, 0.3);
}

.btn-close-custom i {
  color: white;
  font-size: 1rem;
}

/* Progress Bar */
.progress-container {
  padding: 1rem 1.5rem;
  background: #f8f9fa;
  border-bottom: 1px solid #eee;
}

.progress-bar {
  height: 6px;
  background: #e9ecef;
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 8px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #07c232, #0ed145);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.step-labels {
  display: flex;
  justify-content: space-between;
}

.step-label {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #e9ecef;
  color: #6c757d;
  font-size: 0.75rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.step-label.active {
  background: #07c232;
  color: white;
}

.step-label.current {
  box-shadow: 0 0 0 3px rgba(7, 194, 50, 0.3);
}

/* Modal Body */
.ai-help-modal .modal-body {
  padding: 1.5rem;
  max-height: 60vh;
  overflow-y: auto;
}

/* Loading State */
.loading-state {
  text-align: center;
  padding: 3rem 1rem;
}

.loading-animation {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 1rem;
}

.loading-circle {
  width: 12px;
  height: 12px;
  background: #07c232;
  border-radius: 50%;
  animation: bounce 1.4s infinite ease-in-out both;
}

.loading-circle:nth-child(1) {
  animation-delay: -0.32s;
}
.loading-circle:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes bounce {
  0%,
  80%,
  100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}

.loading-text {
  color: #6c757d;
  font-size: 1rem;
}

/* Start State */
.start-state {
  text-align: center;
  padding: 2rem 1rem;
}

.start-icon {
  margin-bottom: 1.5rem;
}

.start-icon img {
  width: 100px;
  height: 100px;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 0.9;
  }
  50% {
    transform: scale(1.05);
    opacity: 1;
  }
}

.start-title {
  color: #333;
  font-size: 1.4rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.start-subtitle {
  color: #6c757d;
  font-size: 1rem;
  margin-bottom: 1.5rem;
}

.btn-start {
  background: linear-gradient(135deg, #07c232 0%, #05a028 100%);
  color: white;
  border: none;
  padding: 12px 32px;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  transition: all 0.3s;
  box-shadow: 0 4px 15px rgba(7, 194, 50, 0.3);
}

.btn-start:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(7, 194, 50, 0.4);
}

/* Content State */
.content-state {
  padding: 0.5rem 0;
}

.step-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #f0f0f0;
}

.step-badge {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #07c232 0%, #05a028 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.1rem;
}

.step-title {
  color: #07c232;
  font-size: 1.15rem;
  font-weight: 600;
  margin: 0;
}

.content-wrapper {
  background: #fafbfc;
  border-radius: 12px;
  padding: 1.25rem;
  border: 1px solid #e9ecef;
  max-height: 45vh;
  overflow-y: auto;
}

/* AI Content Styling */
.ai-content {
  font-size: 1.05rem;
  line-height: 1.9;
  color: #333;
}

.ai-content.text-right {
  text-align: right;
}

.ai-content.text-left {
  text-align: left;
}

/* Content Paragraphs */
.ai-content :deep(.content-paragraph) {
  margin-bottom: 1rem;
}

.ai-content :deep(.content-paragraph:last-child) {
  margin-bottom: 0;
}

/* Math Blocks */
.ai-content :deep(.math-block) {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1rem;
  margin: 1rem 0;
  overflow-x: auto;
  text-align: center;
}

.ai-content :deep(.math-inline) {
  background: rgba(7, 194, 50, 0.08);
  padding: 2px 6px;
  border-radius: 4px;
}

/* Lists */
.ai-content :deep(.list-number) {
  color: #07c232;
  font-weight: 700;
  margin-left: 4px;
}

.ai-content :deep(.list-bullet) {
  color: #07c232;
  font-weight: 700;
  margin-left: 8px;
}

/* Highlights */
.ai-content :deep(.highlight) {
  color: #07c232;
  background: rgba(7, 194, 50, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
}

/* MathJax Styling */
.ai-content :deep(mjx-container) {
  font-size: 1.1em !important;
  margin: 0.2em 0;
}

.ai-content :deep(.MathJax) {
  font-size: 1.1em !important;
}

/* Content Loading Skeleton */
.content-loading {
  position: relative;
  padding: 1rem;
  overflow: hidden;
}

.skeleton-lines {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.skeleton-line {
  height: 16px;
  background: linear-gradient(90deg, #e8f5e9 25%, #c8e6c9 50%, #e8f5e9 75%);
  background-size: 200% 100%;
  border-radius: 8px;
  animation: skeleton-pulse 1.5s ease-in-out infinite;
}

.skeleton-line:nth-child(1) {
  animation-delay: 0s;
}
.skeleton-line:nth-child(2) {
  animation-delay: 0.1s;
}
.skeleton-line:nth-child(3) {
  animation-delay: 0.2s;
}
.skeleton-line:nth-child(4) {
  animation-delay: 0.3s;
}
.skeleton-line:nth-child(5) {
  animation-delay: 0.4s;
}

@keyframes skeleton-pulse {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.loading-shimmer {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(7, 194, 50, 0.08) 50%,
    transparent 100%
  );
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

/* Fade In Animation */
.fade-in {
  animation: fadeIn 0.4s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Dark Mode Support for Skeleton Loading */
:root[data-theme="dark"] .skeleton-line,
.dark .skeleton-line,
[data-bs-theme="dark"] .skeleton-line {
  background: linear-gradient(90deg, #1e3a1e 25%, #2d4a2d 50%, #1e3a1e 75%);
  background-size: 200% 100%;
}

:root[data-theme="dark"] .loading-shimmer,
.dark .loading-shimmer,
[data-bs-theme="dark"] .loading-shimmer {
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(7, 194, 50, 0.15) 50%,
    transparent 100%
  );
}

:root[data-theme="dark"] .content-wrapper,
.dark .content-wrapper,
[data-bs-theme="dark"] .content-wrapper {
  background: #1a2e1a;
  border-color: rgba(7, 194, 50, 0.2);
}

:root[data-theme="dark"] .ai-content,
.dark .ai-content,
[data-bs-theme="dark"] .ai-content {
  color: #e0e0e0;
}

:root[data-theme="dark"] .ai-content :deep(.math-block),
.dark .ai-content :deep(.math-block),
[data-bs-theme="dark"] .ai-content :deep(.math-block) {
  background: #0d1f0d;
  border-color: rgba(7, 194, 50, 0.2);
}

:root[data-theme="dark"] .ai-content :deep(.math-inline),
.dark .ai-content :deep(.math-inline),
[data-bs-theme="dark"] .ai-content :deep(.math-inline) {
  background: rgba(7, 194, 50, 0.15);
}

:root[data-theme="dark"] .ai-content :deep(.highlight),
.dark .ai-content :deep(.highlight),
[data-bs-theme="dark"] .ai-content :deep(.highlight) {
  background: rgba(7, 194, 50, 0.2);
  color: #4ade80;
}

:root[data-theme="dark"] .step-header,
.dark .step-header,
[data-bs-theme="dark"] .step-header {
  border-bottom-color: rgba(7, 194, 50, 0.2);
}

:root[data-theme="dark"] .step-title,
.dark .step-title,
[data-bs-theme="dark"] .step-title {
  color: #4ade80;
}

:root[data-theme="dark"] .modal-content,
.dark .modal-content,
[data-bs-theme="dark"] .modal-content {
  background: #121212;
}

:root[data-theme="dark"] .progress-container,
.dark .progress-container,
[data-bs-theme="dark"] .progress-container {
  background: #1a1a1a;
  border-bottom-color: rgba(255, 255, 255, 0.1);
}

:root[data-theme="dark"] .progress-bar,
.dark .progress-bar,
[data-bs-theme="dark"] .progress-bar {
  background: #2a2a2a;
}

:root[data-theme="dark"] .step-label,
.dark .step-label,
[data-bs-theme="dark"] .step-label {
  background: #2a2a2a;
  color: #888;
}

:root[data-theme="dark"] .modal-footer,
.dark .modal-footer,
[data-bs-theme="dark"] .modal-footer {
  background: #1a1a1a;
  border-top-color: rgba(255, 255, 255, 0.1);
}

:root[data-theme="dark"] .btn-prev,
.dark .btn-prev,
[data-bs-theme="dark"] .btn-prev {
  background: #2a2a2a;
  color: #aaa;
  border-color: #3a3a3a;
}

:root[data-theme="dark"] .btn-prev:hover:not(:disabled),
.dark .btn-prev:hover:not(:disabled),
[data-bs-theme="dark"] .btn-prev:hover:not(:disabled) {
  background: #3a3a3a;
  border-color: #4a4a4a;
}

:root[data-theme="dark"] .dot,
.dark .dot,
[data-bs-theme="dark"] .dot {
  background: #3a3a3a;
}

:root[data-theme="dark"] .start-title,
.dark .start-title,
[data-bs-theme="dark"] .start-title {
  color: #e0e0e0;
}

:root[data-theme="dark"] .start-subtitle,
.dark .start-subtitle,
[data-bs-theme="dark"] .start-subtitle {
  color: #888;
}

:root[data-theme="dark"] .ai-help-modal .modal-content,
.dark .ai-help-modal .modal-content,
[data-bs-theme="dark"] .ai-help-modal .modal-content {
  background: #1a1a1a;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

:root[data-theme="dark"] .ai-help-modal .modal-body,
.dark .ai-help-modal .modal-body,
[data-bs-theme="dark"] .ai-help-modal .modal-body {
  background: #1a1a1a;
}

:root[data-theme="dark"] .ai-help-modal .modal-header,
.dark .ai-help-modal .modal-header,
[data-bs-theme="dark"] .ai-help-modal .modal-header {
  background: linear-gradient(135deg, #059028 0%, #047020 100%);
}

:root[data-theme="dark"] .loading-state,
.dark .loading-state,
[data-bs-theme="dark"] .loading-state {
  background: #1a1a1a;
}

:root[data-theme="dark"] .loading-text,
.dark .loading-text,
[data-bs-theme="dark"] .loading-text {
  color: #aaa;
}

:root[data-theme="dark"] .start-state,
.dark .start-state,
[data-bs-theme="dark"] .start-state {
  background: #1a1a1a;
}

:root[data-theme="dark"] .content-state,
.dark .content-state,
[data-bs-theme="dark"] .content-state {
  background: #1a1a1a;
}

:root[data-theme="dark"] .step-badge,
.dark .step-badge,
[data-bs-theme="dark"] .step-badge {
  background: linear-gradient(135deg, #059028 0%, #047020 100%);
}

:root[data-theme="dark"] .ai-help-modal .modal-footer,
.dark .ai-help-modal .modal-footer,
[data-bs-theme="dark"] .ai-help-modal .modal-footer {
  background: #121212;
  border-top-color: rgba(255, 255, 255, 0.1);
}

:root[data-theme="dark"] .btn-next,
.dark .btn-next,
[data-bs-theme="dark"] .btn-next,
:root[data-theme="dark"] .btn-finish,
.dark .btn-finish,
[data-bs-theme="dark"] .btn-finish {
  background: linear-gradient(135deg, #059028 0%, #047020 100%);
  box-shadow: 0 4px 12px rgba(7, 194, 50, 0.2);
}

:root[data-theme="dark"] .btn-start,
.dark .btn-start,
[data-bs-theme="dark"] .btn-start {
  background: linear-gradient(135deg, #059028 0%, #047020 100%);
  box-shadow: 0 4px 15px rgba(7, 194, 50, 0.2);
}

:root[data-theme="dark"] .ai-help-modal .progress-container,
.dark .ai-help-modal .progress-container,
[data-bs-theme="dark"] .ai-help-modal .progress-container {
  background: #121212;
  border-bottom-color: rgba(255, 255, 255, 0.1);
}

:root[data-theme="dark"] .ai-help-modal .progress-bar,
.dark .ai-help-modal .progress-bar,
[data-bs-theme="dark"] .ai-help-modal .progress-bar {
  background: #2a2a2a;
}

:root[data-theme="dark"] .ai-help-modal .step-label,
.dark .ai-help-modal .step-label,
[data-bs-theme="dark"] .ai-help-modal .step-label {
  background: #2a2a2a;
  color: #888;
}

:root[data-theme="dark"] .ai-help-modal .step-label.active,
.dark .ai-help-modal .step-label.active,
[data-bs-theme="dark"] .ai-help-modal .step-label.active {
  background: #059028;
  color: white;
}

:root[data-theme="dark"] .ai-help-modal .step-label.current,
.dark .ai-help-modal .step-label.current,
[data-bs-theme="dark"] .ai-help-modal .step-label.current {
  box-shadow: 0 0 0 3px rgba(7, 194, 50, 0.4);
}

:root[data-theme="dark"] .dot.active,
.dark .dot.active,
[data-bs-theme="dark"] .dot.active {
  background: #059028;
}

:root[data-theme="dark"] .dot.completed,
.dark .dot.completed,
[data-bs-theme="dark"] .dot.completed {
  background: rgba(7, 194, 50, 0.4);
}

:root[data-theme="dark"] .ai-content :deep(.content-paragraph),
.dark .ai-content :deep(.content-paragraph),
[data-bs-theme="dark"] .ai-content :deep(.content-paragraph) {
  color: #e0e0e0;
}

:root[data-theme="dark"] .ai-content :deep(.list-number),
.dark .ai-content :deep(.list-number),
[data-bs-theme="dark"] .ai-content :deep(.list-number) {
  color: #4ade80;
}

:root[data-theme="dark"] .ai-content :deep(.list-bullet),
.dark .ai-content :deep(.list-bullet),
[data-bs-theme="dark"] .ai-content :deep(.list-bullet) {
  color: #4ade80;
}

/* Scrollbar Dark Mode */
:root[data-theme="dark"]
  .ai-help-modal
  .content-wrapper::-webkit-scrollbar-track,
.dark .ai-help-modal .content-wrapper::-webkit-scrollbar-track,
[data-bs-theme="dark"]
  .ai-help-modal
  .content-wrapper::-webkit-scrollbar-track {
  background: rgba(7, 194, 50, 0.1) !important;
}

:root[data-theme="dark"]
  .ai-help-modal
  .content-wrapper::-webkit-scrollbar-thumb,
.dark .ai-help-modal .content-wrapper::-webkit-scrollbar-thumb,
[data-bs-theme="dark"]
  .ai-help-modal
  .content-wrapper::-webkit-scrollbar-thumb {
  background: #059028 !important;
}

:root[data-theme="dark"] .ai-help-modal .modal-body::-webkit-scrollbar-track,
.dark .ai-help-modal .modal-body::-webkit-scrollbar-track,
[data-bs-theme="dark"] .ai-help-modal .modal-body::-webkit-scrollbar-track {
  background: rgba(7, 194, 50, 0.1) !important;
}

:root[data-theme="dark"] .ai-help-modal .modal-body::-webkit-scrollbar-thumb,
.dark .ai-help-modal .modal-body::-webkit-scrollbar-thumb,
[data-bs-theme="dark"] .ai-help-modal .modal-body::-webkit-scrollbar-thumb {
  background: #059028 !important;
}

/* Footer */
.ai-help-modal .modal-footer {
  background: #f8f9fa;
  border-top: 1px solid #eee;
  padding: 1rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.btn-nav {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 50px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn-prev {
  background: white;
  color: #6c757d;
  border: 2px solid #dee2e6;
}

.btn-prev:hover:not(:disabled) {
  background: #f8f9fa;
  border-color: #adb5bd;
}

.btn-prev:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-next,
.btn-finish {
  background: linear-gradient(135deg, #07c232 0%, #05a028 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(7, 194, 50, 0.3);
}

.btn-next:hover,
.btn-finish:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(7, 194, 50, 0.4);
}

/* Step Dots */
.step-dots {
  display: flex;
  gap: 8px;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #dee2e6;
  transition: all 0.2s;
}

.dot.active {
  background: #07c232;
  transform: scale(1.2);
}

.dot.completed {
  background: rgba(7, 194, 50, 0.5);
}

/* Scrollbar Styling - Content Wrapper */
.ai-help-modal .content-wrapper {
  scrollbar-width: thin !important;
  scrollbar-color: #07c232 rgba(7, 194, 50, 0.15) !important;
}

.ai-help-modal .content-wrapper::-webkit-scrollbar {
  width: 8px !important;
  display: block !important;
}

.ai-help-modal .content-wrapper::-webkit-scrollbar-track {
  background: rgba(7, 194, 50, 0.15) !important;
  border-radius: 4px !important;
}

.ai-help-modal .content-wrapper::-webkit-scrollbar-thumb {
  background: #07c232 !important;
  border-radius: 4px !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
}

.ai-help-modal .content-wrapper::-webkit-scrollbar-thumb:hover {
  background: #05a028 !important;
}

/* Scrollbar Styling - Modal Body */
.ai-help-modal .modal-body {
  scrollbar-width: thin !important;
  scrollbar-color: #07c232 rgba(7, 194, 50, 0.15) !important;
}

.ai-help-modal .modal-body::-webkit-scrollbar {
  width: 8px !important;
  display: block !important;
}

.ai-help-modal .modal-body::-webkit-scrollbar-track {
  background: rgba(7, 194, 50, 0.15) !important;
  border-radius: 4px !important;
}

.ai-help-modal .modal-body::-webkit-scrollbar-thumb {
  background: #07c232 !important;
  border-radius: 4px !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
}

.ai-help-modal .modal-body::-webkit-scrollbar-thumb:hover {
  background: #05a028 !important;
}

/* Responsive */
@media (max-width: 768px) {
  .ai-help-modal .modal-dialog {
    margin: 0.5rem;
    max-width: calc(100% - 1rem);
  }

  .ai-help-modal .modal-content {
    max-height: 95vh;
  }

  .ai-help-modal .modal-body {
    max-height: 55vh;
  }

  .content-wrapper {
    max-height: 40vh;
  }

  .btn-nav span {
    display: none;
  }

  .btn-nav {
    padding: 10px 16px;
  }
}
</style>
